import React from 'react';
import { Tooltip } from 'antd';
import { Mode } from '../hooks/useP922AMode';

interface ActionBarProps {
  mode: Mode;
  isEditing: boolean;
  record: unknown;
  currentIdx: number;
  allIdsLength: number;
  permissions: {
    canView: boolean;
    canCreate: boolean;
    canEdit: boolean;
    canDelete: boolean;
    canPrint: boolean;
  };
  onAdd: () => void;
  onModify: () => void;
  onDelete: () => void;
  onPrint: () => void;
  onNavTo: (idx: number) => void;
  onExit: () => void;
}

/** Pwtcmd1 — bottom action bar */
const ActionBar: React.FC<ActionBarProps> = React.memo(({
  mode, isEditing, record, currentIdx, allIdsLength, permissions,
  onAdd, onModify, onDelete, onPrint, onNavTo, onExit,
}) => {
  const PermBtn: React.FC<{
    label: string; onClick: () => void; disabled: boolean; allowed: boolean;
    title?: string; danger?: boolean;
  }> = ({ label, onClick, disabled, allowed, title, danger }) => {
    const btn = (
      <button
        className={`action-btn${danger ? ' danger' : ''}`}
        onClick={onClick}
        disabled={disabled || !allowed}
      >
        {label}
      </button>
    );
    if (!allowed) {
      return <Tooltip title={title || '無此權限'}>{btn}</Tooltip>;
    }
    return btn;
  };

  // Dual-state labels
  const addLabel = isEditing && mode === 'add' ? '存檔' : '新增';
  const modLabel = isEditing ? '還原' : '修改';

  return (
    <div className="action-bar">
      <div className="action-bar-inner">
        {/* Navigation */}
        <div className="action-group">
          <PermBtn label="最前" onClick={() => onNavTo(0)} disabled={isEditing || currentIdx <= 0} allowed={permissions.canView} />
          <PermBtn label="上一筆" onClick={() => onNavTo(currentIdx - 1)} disabled={isEditing || currentIdx <= 0} allowed={permissions.canView} />
          <span className="nav-counter">{allIdsLength > 0 ? `${currentIdx + 1} / ${allIdsLength}` : '0 / 0'}</span>
          <PermBtn label="下一筆" onClick={() => onNavTo(currentIdx + 1)} disabled={isEditing || currentIdx >= allIdsLength - 1} allowed={permissions.canView} />
          <PermBtn label="最後" onClick={() => onNavTo(allIdsLength - 1)} disabled={isEditing || currentIdx >= allIdsLength - 1} allowed={permissions.canView} />
        </div>

        <div className="action-separator" />

        {/* CRUD */}
        <div className="action-group">
          <PermBtn
            label={addLabel}
            onClick={onAdd}
            disabled={isEditing && mode === 'edit'}
            allowed={mode === 'add' ? permissions.canCreate : permissions.canCreate}
          />
          <PermBtn
            label={modLabel}
            onClick={onModify}
            disabled={!record && !isEditing}
            allowed={isEditing ? true : permissions.canEdit}
          />
          <PermBtn
            label="刪除"
            onClick={onDelete}
            disabled={!record || isEditing}
            allowed={permissions.canDelete}
            danger
          />
        </div>

        <div className="action-separator" />

        {/* Print + Exit */}
        <div className="action-group">
          <PermBtn label="列印" onClick={onPrint} disabled={!record || isEditing} allowed={permissions.canPrint} />
          <button className="action-btn" onClick={onExit}>離開</button>
        </div>
      </div>
    </div>
  );
});

ActionBar.displayName = 'ActionBar';
export default ActionBar;
